import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { CheckCircle, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientSignup() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [clientName, setClientName] = useState('');
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirm_password: ''
  });

  useEffect(() => {
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await axios.get(`${API}/invite-links/validate/${token}`);
      setValid(true);
      setClientName(response.data.client_name);
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Invalid or expired invite link';
      const errorData = error.response?.data;
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        } else if (typeof errorData.detail === 'object' && errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        }
      }
      
      toast.error(errorMessage);
      setValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setSubmitting(true);

    try {
      const response = await axios.post(`${API}/client-signup`, {
        invite_token: token,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name
      });

      // Store token and user with correct keys for client portal
      localStorage.setItem('client_token', response.data.access_token);
      localStorage.setItem('client_user', JSON.stringify(response.data.user));
      
      // Set axios default header immediately
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      
      toast.success('Account created! Setting up your profile...');
      
      // Redirect to onboarding immediately
      window.location.href = '/app/onboarding';
    } catch (error) {
      // Handle different error response formats
      let errorMessage = 'Failed to create account';
      const errorData = error.response?.data;
      
      if (errorData) {
        if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map(e => e.msg || e.message || 'Validation error').join(', ');
        } else if (typeof errorData.detail === 'object' && errorData.detail?.msg) {
          errorMessage = errorData.detail.msg;
        }
      }
      
      toast.error(errorMessage);
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="pt-6 text-center">
            <Loader className="h-8 w-8 animate-spin mx-auto text-[#1a61e8]" />
            <p className="mt-4 text-gray-600">Validating invite link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!valid) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <p className="text-red-600 font-semibold text-lg">Invalid or expired invite link</p>
            <p className="text-gray-500 mt-2">Please contact your coach for a new invitation</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <img src="/logo.png" alt="MealGuide" className="h-32 mx-auto mb-2" />
          <p className="text-gray-600">Your personalized nutrition journey</p>
        </div>

        <Card className="border-0 shadow-2xl">
          <CardHeader className="space-y-1 pb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-[#1a61e8]/10 flex items-center justify-center">
                <CheckCircle className="h-8 w-8 text-[#1a61e8]" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center font-bold text-gray-900">Create Your Account</CardTitle>
            <CardDescription className="text-center text-base">
              Welcome, <span className="font-semibold text-[#1a61e8]">{clientName}</span>! Let's get you set up
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="first_name" className="text-gray-700">First Name</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    required
                    disabled={submitting}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="last_name" className="text-gray-700">Last Name</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    required
                    disabled={submitting}
                    className="mt-1"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-gray-700">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={submitting}
                  className="mt-1"
                  placeholder="your@email.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-gray-700">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  disabled={submitting}
                  className="mt-1"
                  placeholder="Minimum 6 characters"
                />
                <p className="text-xs text-gray-500 mt-1.5">Must be at least 6 characters</p>
              </div>

              <div>
                <Label htmlFor="confirm_password" className="text-gray-700">Confirm Password</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={formData.confirm_password}
                  onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                  required
                  disabled={submitting}
                  className="mt-1"
                  placeholder="Re-enter your password"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base bg-[#1a61e8] hover:bg-[#1552c7] mt-6"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <Loader className="h-5 w-5 mr-2 animate-spin" />
                    Creating Your Account...
                  </>
                ) : (
                  'Create Account & Continue'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-gray-500 mt-6">
          By creating an account, you agree to our Terms of Service
        </p>
      </div>
    </div>
  );
}
